import { type ComponentType } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import {
  Home,
  Music,
  Users,
  FileText,
  BarChart3,
  Activity,
  Database,
  Archive,
  AlertTriangle,
  CreditCard,
  MessageCircle,
  Layers,
  Zap,
  Image,
  FileBarChart,
  MessageSquare,
  ClipboardList,
} from "lucide-react";
import { cn } from "@sonify/web-ui";
import { GroupedDropdown } from "@/shared/components";

// ============================================
// Types
// ============================================

interface NavItem {
  label: string;
  path: string;
  icon: ComponentType<{ size?: number }>;
}

interface NavGroup {
  label: string;
  icon: ComponentType<{ size?: number }>;
  items: NavItem[];
}

type NavEntry = NavItem | NavGroup;

function isGroup(entry: NavEntry): entry is NavGroup {
  return "items" in entry;
}

// ============================================
// Navigation Config
// ============================================

const NAV_CONFIG: NavEntry[] = [
  { label: "Dashboard", path: "/", icon: Home },
  {
    label: "Content",
    icon: Layers,
    items: [
      { label: "Songs", path: "/songs", icon: Music },
      { label: "Users", path: "/users", icon: Users },
      { label: "Briefs", path: "/briefs", icon: FileText },
    ],
  },
  {
    label: "Platform",
    icon: BarChart3,
    items: [
      { label: "SoundScore", path: "/soundscore", icon: BarChart3 },
      { label: "Catalog", path: "/catalog", icon: Archive },
      { label: "Billing", path: "/billing", icon: CreditCard },
      { label: "AVA", path: "/ava", icon: MessageCircle },
    ],
  },
  {
    label: "Ops",
    icon: Activity,
    items: [
      { label: "Errors", path: "/errors", icon: AlertTriangle },
      { label: "Diagnostics", path: "/diagnostics", icon: Activity },
      { label: "Seed", path: "/seed", icon: Database },
      { label: "Jobs", path: "/jobs", icon: Zap },
      { label: "Assets", path: "/system-assets", icon: Image },
    ],
  },
  {
    label: "Data",
    icon: ClipboardList,
    items: [
      { label: "Reports", path: "/reports", icon: FileBarChart },
      { label: "Feedback", path: "/feedback", icon: MessageSquare },
    ],
  },
];

export { NAV_CONFIG, isGroup, type NavItem, type NavGroup, type NavEntry };

// ============================================
// Components
// ============================================

function StandaloneNavItem({ item }: { item: NavItem }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-1.5 px-3 text-sm font-medium transition-colors border-b-2 -mb-px",
          isActive
            ? "border-foreground-accent text-foreground"
            : "border-transparent text-foreground-muted hover:text-foreground hover:border-foreground-muted/30",
        )
      }
    >
      <Icon size={14} />
      {item.label}
    </NavLink>
  );
}

function NavGroupDropdown({ group }: { group: NavGroup }) {
  const location = useLocation();

  const activeItem = group.items.find(
    (item) => location.pathname === item.path || location.pathname.startsWith(item.path + "/"),
  );

  return (
    <GroupedDropdown
      label={group.label}
      icon={group.icon}
      items={group.items.map((item) => ({
        key: item.path,
        label: item.label,
        icon: item.icon,
      }))}
      activeKey={activeItem?.path ?? null}
      onSelect={() => {}}
      renderItem={(item, isActive) => {
        const ItemIcon = item.icon;
        return (
          <Link
            to={item.key}
            className={cn(
              "flex items-center gap-2 cursor-pointer",
              isActive && "text-foreground-accent",
            )}
          >
            {ItemIcon && <ItemIcon size={14} />}
            {item.label}
          </Link>
        );
      }}
    />
  );
}

export function TopNav() {
  return (
    <nav className="hidden md:flex items-stretch gap-1">
      {NAV_CONFIG.map((entry) =>
        isGroup(entry) ? (
          <NavGroupDropdown key={entry.label} group={entry} />
        ) : (
          <StandaloneNavItem key={entry.label} item={entry} />
        ),
      )}
    </nav>
  );
}
